import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Paper, Typography, Stack, Chip, alpha, LinearProgress, Skeleton, useTheme } from '@mui/material';
import { Bolt, TrendingUp, CheckCircle, Cancel, Schedule } from '@mui/icons-material';
import { useTenantCurrency } from '../../hooks/useTenantCurrency';
import {
  aggregateJobStats,
  formatActiveHint,
  formatPendingHint,
  formatPercent,
  resolveActiveJobs,
  resolvePendingJobs,
} from '../../utils/jobStats';
import { DASHBOARD_NAV } from '../../config/dashboardNavigation';
import type { DashboardOverview } from './DashboardStatsGrid';

interface LiveOps {
  activeJobs?: number;
  pendingJobs?: number;
  completedJobs?: number;
  cancelledJobs?: number;
  completionRate?: number;
  cancelRate?: number;
  totalRevenue?: number;
  totalVolume?: number;
}

interface Props {
  title: string;
  subtitle: string;
  eyebrow: string;
  liveOps?: LiveOps;
  overview?: DashboardOverview;
  enabledModules?: string[];
  isSuperAdmin?: boolean;
  role?: string;
  loading?: boolean;
  accent?: string;
}

export default function DashboardGodViewHeader({
  title,
  subtitle,
  eyebrow,
  liveOps = {},
  overview,
  enabledModules = [],
  isSuperAdmin,
  role,
  loading,
  accent = '#2563eb',
}: Props) {
  const theme = useTheme();
  const { formatAmount } = useTenantCurrency();
  const isLight = theme.palette.mode === 'light';

  const jobStats = useMemo(
    () => aggregateJobStats(overview, enabledModules, { isSuperAdmin, role }),
    [overview, enabledModules, isSuperAdmin, role],
  );

  const hasCancelData = useMemo(
    () => [
      overview?.cancelledRides,
      overview?.cancelledFoodOrders,
      overview?.cancelledGroceryOrders,
      overview?.cancelledPorterOrders,
      overview?.cancelledOnDemandBookings,
    ].some((value) => value != null),
    [overview],
  );

  const completionRate = !loading
    ? jobStats.completionRate
    : (liveOps.completionRate ?? 0);
  const cancelRate = !loading && hasCancelData
    ? jobStats.cancelRate
    : (liveOps.cancelRate ?? 0);
  const completedJobs = !loading
    ? jobStats.completedJobs
    : (liveOps.completedJobs ?? 0);
  const cancelledJobs = !loading && hasCancelData
    ? jobStats.cancelledJobs
    : (liveOps.cancelledJobs ?? 0);
  const activeJobs = !loading
    ? resolveActiveJobs(overview, enabledModules, { isSuperAdmin, role })
    : (liveOps.activeJobs ?? 0);
  const pendingJobs = !loading
    ? resolvePendingJobs(overview, enabledModules, { isSuperAdmin, role })
    : (liveOps.pendingJobs ?? 0);
  const totalJobs = !loading
    ? jobStats.totalJobs
    : (liveOps.totalVolume ?? 0);

  const kpis = [
    {
      label: 'Live jobs',
      value: activeJobs,
      hint: formatActiveHint(activeJobs, jobStats.activeByModule),
      icon: Bolt,
      color: '#0891b2',
      to: DASHBOARD_NAV.liveJobs,
    },
    {
      label: 'Pending',
      value: pendingJobs,
      hint: formatPendingHint(pendingJobs, jobStats.pendingByModule),
      icon: Schedule,
      color: '#d97706',
      to: DASHBOARD_NAV.pending,
    },
    {
      label: 'Completion',
      value: formatPercent(completionRate),
      hint: totalJobs > 0 ? `${completedJobs.toLocaleString()} / ${totalJobs.toLocaleString()} jobs` : 'No jobs yet',
      icon: CheckCircle,
      color: '#059669',
      showProgress: true,
      to: DASHBOARD_NAV.completion,
    },
    {
      label: 'Cancel rate',
      value: formatPercent(cancelRate),
      hint: totalJobs > 0 ? `${cancelledJobs.toLocaleString()} / ${totalJobs.toLocaleString()} jobs` : 'No jobs yet',
      icon: Cancel,
      color: '#dc2626',
      showProgress: true,
      to: DASHBOARD_NAV.cancelRate,
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        p: { xs: 2.5, md: 3.5 },
        borderRadius: '20px',
        overflow: 'hidden',
        position: 'relative',
        border: `1px solid ${alpha(accent, 0.18)}`,
        background: isLight
          ? `linear-gradient(135deg, ${alpha(accent, 0.09)} 0%, ${alpha(accent, 0.03)} 40%, ${alpha('#059669', 0.04)} 100%)`
          : `linear-gradient(135deg, ${alpha(accent, 0.18)} 0%, ${alpha(accent, 0.07)} 40%, ${alpha('#059669', 0.06)} 100%)`,
      }}
    >
      {/* Background decorative elements */}
      <Box sx={{
        position: 'absolute',
        top: -60,
        right: -60,
        width: 220,
        height: 220,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${alpha(accent, 0.15)} 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <Box sx={{
        position: 'absolute',
        bottom: -40,
        left: '30%',
        width: 160,
        height: 160,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${alpha('#059669', 0.09)} 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems={{ xs: 'flex-start', lg: 'flex-start' }} justifyContent="space-between">
          <Box flex={1} minWidth={0}>
            <Chip
              size="small"
              label={eyebrow}
              sx={{
                mb: 1.5,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontSize: '0.65rem',
                bgcolor: alpha(accent, 0.12),
                color: accent,
                border: `1px solid ${alpha(accent, 0.25)}`,
                borderRadius: '8px',
              }}
            />
            <Typography
              variant="h4"
              fontWeight={900}
              sx={{
                letterSpacing: '-0.025em',
                mb: 1,
                fontSize: { xs: '1.5rem', sm: '2rem' },
                background: isLight
                  ? `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${alpha(theme.palette.text.primary, 0.75)} 100%)`
                  : 'linear-gradient(135deg, #e8edf5 0%, rgba(232,237,245,0.75) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {title}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: { xs: '100%', md: 480 }, lineHeight: 1.6 }}>
              {subtitle}
            </Typography>
            {!loading && (liveOps.totalRevenue ?? 0) > 0 && (
              <Stack direction="row" alignItems="center" spacing={1} mt={2} flexWrap="wrap" useFlexGap>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: '10px',
                  bgcolor: alpha('#059669', 0.1),
                  border: `1px solid ${alpha('#059669', 0.2)}`,
                }}>
                  <TrendingUp sx={{ color: '#059669', fontSize: 18 }} />
                  <Typography variant="subtitle2" fontWeight={800} color="success.main">
                    {formatAmount(liveOps.totalRevenue ?? 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    · {(liveOps.totalVolume ?? 0).toLocaleString()} jobs
                  </Typography>
                </Box>
              </Stack>
            )}
          </Box>
        </Stack>

        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr 1fr', md: 'repeat(4, 1fr)' }}
          gap={1.5}
          mt={3}
        >
          {kpis.map((kpi) => {
            const Icon = kpi.icon;
            return (
              <Paper
                key={kpi.label}
                elevation={0}
                component={RouterLink}
                to={kpi.to}
                sx={{
                  p: 2,
                  borderRadius: '14px',
                  border: `1px solid ${alpha(kpi.color, 0.18)}`,
                  bgcolor: isLight ? alpha(kpi.color, 0.05) : alpha(kpi.color, 0.1),
                  cursor: 'pointer',
                  textDecoration: 'none',
                  color: 'inherit',
                  display: 'block',
                  transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${alpha(kpi.color, 0.16)}`,
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${alpha(kpi.color, 0.55)}`,
                    outlineOffset: 2,
                  },
                }}
              >
                {loading ? (
                  <Skeleton height={52} sx={{ borderRadius: '8px' }} />
                ) : (
                  <>
                    <Stack direction="row" alignItems="center" spacing={0.75} mb={1}>
                      <Box sx={{
                        p: 0.625,
                        borderRadius: '8px',
                        bgcolor: alpha(kpi.color, 0.12),
                        display: 'flex',
                        alignItems: 'center',
                      }}>
                        <Icon sx={{ fontSize: 15, color: kpi.color }} />
                      </Box>
                      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {kpi.label}
                      </Typography>
                    </Stack>
                    <Typography variant="h5" fontWeight={900} sx={{ color: kpi.color, letterSpacing: '-0.02em' }}>
                      {kpi.value}
                    </Typography>
                    {'hint' in kpi && kpi.hint && (
                      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                        {kpi.hint}
                      </Typography>
                    )}
                    {'showProgress' in kpi && kpi.showProgress && (
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(
                          kpi.label === 'Completion' ? completionRate : cancelRate,
                          100,
                        )}
                        sx={{
                          mt: 1,
                          height: 4,
                          borderRadius: 2,
                          bgcolor: alpha(kpi.color, 0.12),
                          '& .MuiLinearProgress-bar': { bgcolor: kpi.color },
                        }}
                      />
                    )}
                  </>
                )}
              </Paper>
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
}
